import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Users, 
  UserPlus, 
  Activity, 
  FileText,
  TrendingUp,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { getAppToken } from '@/lib/oauthClient';
import axios from 'axios';

const COLORS = ['#659D9F', '#E17A73', '#F4C465', '#8B7FA8'];

function StatCard({ title, value, icon: Icon, trend, loading }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <h3 className="text-3xl font-bold mt-2">{value || 0}</h3>
            {trend && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {trend}
              </p>
            )}
          </div>
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Use axios which already has the Authorization header set by AuthContext
      const [statsRes, activityRes, usersRes] = await Promise.all([
        axios.get(`${backendUrl}/api/admin/dashboard/stats`),
        axios.get(`${backendUrl}/api/admin/dashboard/activity`),
        axios.get(`${backendUrl}/api/admin/dashboard/recent-users?limit=10`)
      ]);
      
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setRecentUsers(usersRes.data.users);

    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
      if (error.response?.status === 401) {
        console.error('❌ 401 Unauthorized - Token manquant ou invalide');
      }
    } finally {
      setLoading(false);
    }
  };

  const roleData = stats?.roles
    ? Object.entries(stats.roles).map(([name, value]) => ({ name, value }))
    : [];

  const planData = stats?.plans
    ? Object.entries(stats.plans).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Dashboard Administrateur</h1>
        <p className="text-muted-foreground">Vue d'ensemble de l'application Nannyou</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Utilisateurs"
          value={stats?.total_users}
          icon={Users}
          trend={`+${stats?.new_users_7d || 0} cette semaine`}
          loading={loading}
        />
        <StatCard
          title="Nouveaux (7j)"
          value={stats?.new_users_7d}
          icon={UserPlus}
          loading={loading}
        />
        <StatCard
          title="Actifs (7j)"
          value={stats?.active_users_7d}
          icon={Activity}
          loading={loading}
        />
        <StatCard
          title="Contrats Actifs"
          value={stats?.active_contracts}
          icon={FileText}
          trend={`${stats?.total_contracts || 0} total`}
          loading={loading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Inscriptions (30 derniers jours)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={activity?.registrations || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'dd/MM', { locale: fr })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'dd MMMM yyyy', { locale: fr })}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#659D9F" 
                    name="Inscriptions"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Role Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition par Rôle</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={roleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Heures en attente</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.time_entries_pending || 0}</h3>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Paies ce mois</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.payroll_this_month || 0}</h3>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Plan Premium</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.plans?.premium || 0}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.plans?.free || 0} gratuits
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Derniers Utilisateurs</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/users')}
            >
              Voir tout
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Inscription</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(recentUsers || []).map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'destructive' : 'default'}>
                        {user.role === 'admin' ? 'Admin' : user.role === 'support' ? 'Support' : user.role === 'parent' ? 'Parent' : 'Nounou'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={['premium', 'parent_pro', 'nanny_pro'].includes(user.current_plan) ? 'default' : 'secondary'}>
                        {['premium', 'parent_pro', 'nanny_pro'].includes(user.current_plan) ? 'Premium' : 'Gratuit'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: fr })}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        Voir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
